/**
 * What the diagnostics controls show after a click: the "Logs packaged" dialog
 * on success, the app's error toast on failure. Rendered by the surface that
 * owns the `useDiagnostics()` instance — the titlebar.
 *
 * The dialog is the app's own `Modal`, the toast the plugins pane's classes:
 * a result that looked different from every other result in the app would be a
 * small, permanent surprise (see `AboutDialog`).
 */
import { useLocale } from "../i18n/LocaleProvider";
import { Modal } from "./settings/Modal";
import type { Diagnostics } from "../hooks/useDiagnostics";

function ToastErrIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16.5v.01" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function DiagnosticsFeedback({ diagnostics }: { diagnostics: Diagnostics }) {
  const { t } = useLocale();
  const { packaged, error } = diagnostics;

  return (
    <>
      <Modal
        open={packaged !== null}
        onClose={diagnostics.dismissPackaged}
        title={packaged?.title ?? ""}
        footer={
          <button type="button" className="settings-btn" onClick={diagnostics.dismissPackaged}>
            {t("common.close")}
          </button>
        }
      >
        {packaged ? (
          <div className="diagnostics-result">
            {/* The path is what gets pasted into a support message; monospaced
                because it is read character by character. */}
            <p className="diagnostics-result-summary">
              <code>{packaged.summary}</code>
            </p>
            <p className="settings-note">{packaged.contains}</p>
            {packaged.skipped ? <p className="settings-note">{packaged.skipped}</p> : null}
          </div>
        ) : null}
      </Modal>

      {error ? (
        <div className="plugin-toast-stack">
          <div className="plugin-toast is-err" role="alert">
            <span className="plugin-toast-icon" aria-hidden="true">
              <ToastErrIcon />
            </span>
            <span className="plugin-toast-text">{error}</span>
            <button
              type="button"
              className="plugin-toast-close"
              aria-label={t("plugins.toast.dismiss")}
              onClick={diagnostics.dismissError}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
